import { Controller } from "@hotwired/stimulus"
import * as d3 from "d3"

export default class extends Controller {
  static values = { url: String, sources: Object }
  static targets = ["typeSelect", "itemSelect", "graphContainer", "summary", "emptyState"]

  connect() {
    this.currentData = null
    this.buildControls()
    this.bindResize()
  }

  disconnect() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  }

  buildControls() {
    const sources = this.sourcesValue || {}
    const types = Object.keys(sources).filter((type) => sources[type]?.length)

    if (!types.length) {
      this.showEmptyState("Add items to start exploring the graph.")
      return
    }

    this.typeSelectTarget.innerHTML = ""
    types.forEach((type) => {
      const option = document.createElement("option")
      option.value = type
      option.textContent = this.humanize(type)
      this.typeSelectTarget.appendChild(option)
    })

    this.typeSelectTarget.value = types[0]
    this.populateItems(types[0])
  }

  typeChanged() {
    this.populateItems(this.typeSelectTarget.value)
  }

  itemChanged() {
    this.loadSelection()
  }

  populateItems(type) {
    const items = (this.sourcesValue && this.sourcesValue[type]) || []
    this.itemSelectTarget.innerHTML = ""

    items.forEach((item) => {
      const option = document.createElement("option")
      option.value = item.id
      option.textContent = item.label
      this.itemSelectTarget.appendChild(option)
    })

    if (!items.length) {
      this.showEmptyState("No items available for this type.")
      return
    }

    this.itemSelectTarget.value = items[0].id
    this.loadSelection()
  }

  async loadSelection() {
    const type = this.typeSelectTarget.value
    const id = this.itemSelectTarget.value
    if (!type || !id) {
      this.showEmptyState("Select something to explore.")
      return
    }

    this.updateSummary(type, id)
    await this.fetchData(type, id)
  }

  async fetchData(type, id) {
    try {
      this.setLoading(true)
      const query = new URLSearchParams({ focus_type: type, focus_id: id })
      const response = await fetch(`${this.urlValue}?${query.toString()}`)
      const data = await response.json()
      this.currentData = data
      if (!data.nodes || data.nodes.length === 0) {
        this.showEmptyState("No connections found for this item.")
      } else {
        this.hideEmptyState()
        this.renderGraph(data)
      }
    } catch (error) {
      console.error("Failed to load graph data:", error)
      this.showEmptyState("We couldn't load the graph. Try again.")
    } finally {
      this.setLoading(false)
    }
  }

  renderGraph(data) {
    const container = this.graphContainerTarget
    const width = container.clientWidth
    const height = container.clientHeight

    // Clear existing content
    d3.select(container).selectAll("*").remove()

    // Create SVG
    const svg = d3.select(container)
      .append("svg")
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", [0, 0, width, height])

    // Create a group for zoom
    const g = svg.append("g")

    // Define zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.1, 4])
      .on("zoom", (event) => {
        g.attr("transform", event.transform)
      })

    svg.call(zoom)

    // Store zoom for reset
    this.zoom = zoom
    this.svg = svg

    // Create simulation
    const simulation = d3.forceSimulation(data.nodes)
      .force("link", d3.forceLink(data.links).id(d => d.id).distance(120))
      .force("charge", d3.forceManyBody().strength(-320))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force("collision", d3.forceCollide().radius(d => this.nodeRadius(d) + 10))

    // Create links
    const link = g.append("g")
      .attr("class", "links")
      .selectAll("line")
      .data(data.links)
      .join("line")
      .attr("stroke", "var(--graph-link)")
      .attr("stroke-width", d => d.strength || 1.2)
      .attr("stroke-opacity", 0.55)

    // Create nodes
    const node = g.append("g")
      .attr("class", "nodes")
      .selectAll("g")
      .data(data.nodes)
      .join("g")
      .call(d3.drag()
        .on("start", (event, d) => this.dragStarted(event, d, simulation))
        .on("drag", (event, d) => this.dragged(event, d))
        .on("end", (event, d) => this.dragEnded(event, d, simulation)))
      .on("click", (event, d) => this.nodeClicked(d))
      .style("cursor", "pointer")

    // Add circles to nodes
    node.append("circle")
      .attr("r", d => this.nodeRadius(d))
      .attr("fill", d => this.nodeColor(d))
      .attr("stroke", "var(--graph-node-stroke)")
      .attr("stroke-width", 2)

    // Add labels
    node.append("text")
      .text(d => this.truncateLabel(d.label))
      .attr("x", 0)
      .attr("y", d => this.nodeRadius(d) + 16)
      .attr("text-anchor", "middle")
      .attr("fill", "var(--graph-label)")
      .attr("font-family", "Fira Sans, sans-serif")
      .attr("font-size", "11px")

    // Update positions on tick
    simulation.on("tick", () => {
      link
        .attr("x1", d => d.source.x)
        .attr("y1", d => d.source.y)
        .attr("x2", d => d.target.x)
        .attr("y2", d => d.target.y)

      node.attr("transform", d => `translate(${d.x},${d.y})`)
    })
  }

  nodeRadius(node) {
    switch (node.type) {
      case "book":
        return 22
      case "publisher":
      case "collection":
        return 18
      case "author":
      case "memorabilia":
        return 16
      case "tag":
        return 14
      default:
        return 15
    }
  }

  nodeColor(node) {
    const colors = {
      book: "var(--graph-book)",
      author: "var(--graph-author)",
      publisher: "var(--graph-publisher)",
      collection: "var(--graph-collection)",
      tag: node.color || "var(--graph-tag)",
      memorabilia: "var(--graph-memorabilia)"
    }
    return colors[node.type] || "var(--graph-book)"
  }

  truncateLabel(label) {
    return label.length > 20 ? label.substring(0, 17) + "..." : label
  }

  dragStarted(event, d, simulation) {
    if (!event.active) simulation.alphaTarget(0.3).restart()
    d.fx = d.x
    d.fy = d.y
  }

  dragged(event, d) {
    d.fx = event.x
    d.fy = event.y
  }

  dragEnded(event, d, simulation) {
    if (!event.active) simulation.alphaTarget(0)
    d.fx = null
    d.fy = null
  }

  nodeClicked(d) {
    if (d.url) {
      window.location.href = d.url
    }
  }

  resetZoom() {
    if (this.svg && this.zoom) {
      this.svg.transition().duration(750).call(
        this.zoom.transform,
        d3.zoomIdentity
      )
    }
  }

  updateSummary(type, id) {
    const items = (this.sourcesValue && this.sourcesValue[type]) || []
    const item = items.find((entry) => entry.id.toString() === id.toString())
    if (item) {
      this.summaryTarget.textContent = `${this.humanize(type)} • ${item.label}`
    }
  }

  showEmptyState(message) {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.textContent = message
      this.emptyStateTarget.classList.add("is-visible")
    }
    d3.select(this.graphContainerTarget).selectAll("svg").remove()
  }

  hideEmptyState() {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.remove("is-visible")
    }
  }

  setLoading(isLoading) {
    if (!this.graphContainerTarget) return
    this.graphContainerTarget.classList.toggle("is-loading", isLoading)
  }

  bindResize() {
    if (!this.graphContainerTarget) return
    this.resizeObserver = new ResizeObserver(() => {
      if (this.currentData && this.currentData.nodes?.length) {
        this.renderGraph(this.currentData)
      }
    })
    this.resizeObserver.observe(this.graphContainerTarget)
  }

  humanize(value) {
    return value
      .toString()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (match) => match.toUpperCase())
  }
}
